/* eslint-disable css-modules/no-undef-class, css-modules/no-unused-class, no-nested-ternary */
import { FC, useState } from 'react';
import classnames from 'classnames';
import { ContentCard, Tabs, SmallLoader, Divider, Icon, UiLibrary } from 'learning-panel-components';
import { useSelector } from 'react-redux';
import { translate } from 'translations';
import { coreService } from 'network-utils';
import { getPlatformUrl, getTags } from 'utils';
import { LearningPathCardProps as LearningPathCard } from '@components/learningPath/learningPathCard/learningPathCard';
import { SkillTreeCardProps as SkillTreeCard } from '@components/skillTree/skillTreeCard/skillTreeCard';
import { selectIsCxUniversity, selectIsPLG } from '@redux/slices/userSlice';
import { calcCourseProgress } from 'learningPanelUtils';
import CourseBanner from './courseBanner/courseBanner';
import CareerJourneySection from './careerJourney/careerJourneySection';
import styles from './learn.module.scss';

export const getVideoTutorialDuration = (duration: string) => {
    const [hours, minutes, seconds] = duration.split(':').map(Number);
    let time = '';
    if (hours > 0) time += `${hours}h`;
    if (minutes > 0) time += `${minutes}m`;
    if (seconds > 0 && hours === 0) time += `${seconds}s`;
    return time;
};

enum TabsType {
    All,
    InProgress,
    NotStarted,
    Completed
}

const Learn: FC<LearnProps> = props => {
    const {
        isLoading,
        primaryCourse,
        assignedLessons,
        otherCourses,
        unavailableCourses,
        setCertificateCourseUuid,
        setIsCustomCourse,
        learningPaths,
        setLearningPathCertificateDate,
        skillTrees,
        setSkillTreeCertificateDate,
        isLeanUser,
        onContactUsClick
    } = props;
    const [tab, setTab] = useState<number>(TabsType.All);
    const isPLG = useSelector(selectIsPLG);
    const isCxUniversity = useSelector(selectIsCxUniversity);

    const getOtherCoursesByTab = (tabType: TabsType) => {
        if (tabType == TabsType.Completed) {
            return otherCourses.filter(course => course.completionPercentage === 100);
        }

        if (tabType == TabsType.NotStarted) {
            return otherCourses.filter(course => course.completionPercentage === 0);
        }

        if (tabType == TabsType.InProgress) {
            return otherCourses.filter(course => course.completionPercentage > 0 && course.completionPercentage < 100);
        }

        return otherCourses;
    };

    const getCoursesList = (courses: coreService.Course[], isLeanDisabled = false) => {
        return (
            <div className={styles.list}>
                <UiLibrary.Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={5}>
                    {courses?.map((course: coreService.Course) => (
                        <UiLibrary.Grid item key={course.uuid}>
                            <ContentCard
                                uuid={course.uuid}
                                type={'course'}
                                imageUrl={
                                    course.iconUrl
                                        ? course.iconUrl
                                        : `${process.env.PUBLIC_URL}/images/courseDefaultCard.svg`
                                }
                                name={course.name}
                                displayName={course.displayName}
                                tagline={course.tagline}
                                cardUrl={`/app/course?courseUuid=${course.uuid}`}
                                useOverlay={Boolean(course.iconUrl)}
                                count={course.numberOfLessons}
                                timeOrDate={String(course.totalDuration)}
                                status={calcCourseProgress(course)}
                                onCompletionCertificateClick={() => {
                                    setCertificateCourseUuid(course.uuid);
                                    setIsCustomCourse(!course.isSystem);
                                }}
                                isLeanDisabled={isLeanDisabled}
                                hasCertificate={course.hasCertificate}
                            />
                        </UiLibrary.Grid>
                    ))}
                </UiLibrary.Grid>
            </div>
        );
    };

    const completedCount = getOtherCoursesByTab(TabsType.Completed).length;
    const notStartedCount = getOtherCoursesByTab(TabsType.NotStarted).length;
    const allCount = getOtherCoursesByTab(TabsType.All).length;
    const inProgressCount = getOtherCoursesByTab(TabsType.InProgress).length;

    return isLoading ? (
        <div className={classnames(styles['content-wrapper'], styles['loading-wrapper'])}>
            {isLoading && <SmallLoader size="85px" className={styles.loader} />}
        </div>
    ) : (
        <div id="learn-page" className={styles['content-wrapper']}>
            <div className={styles.title}>
                <div className={styles.text}>{translate('PrimaryCourse')}</div>
            </div>
            <div className={styles['course-banner']}>
                <CourseBanner
                    name={primaryCourse?.name}
                    displayName={primaryCourse?.displayName}
                    description={primaryCourse?.description}
                    playNextLessonUrl={
                        primaryCourse?.nextLessonUrl ? `${getPlatformUrl()}${primaryCourse?.nextLessonUrl}` : undefined
                    }
                    courseImage={primaryCourse?.iconUrl ?? `${process.env.PUBLIC_URL}/images/courseDefaultCard.svg`}
                    coins={primaryCourse?.score}
                    completionRate={primaryCourse?.completionPercentage}
                    navigateCoursePageUrl={`/app/course?courseUuid=${primaryCourse?.uuid}`}
                    useOverlay={Boolean(primaryCourse?.iconUrl)}
                />
            </div>

            {!isLeanUser && (
                <div className={styles.title}>
                    <div className={styles.text}>
                        {translate('AssignedLessons')} ({assignedLessons.length})
                    </div>
                </div>
            )}
            {!isLeanUser && (
                <div className={styles.list}>
                    <UiLibrary.Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                    >
                        {assignedLessons.length === 0 ? (
                            <div className={styles['empty-state']}>
                                <div className={styles['title-no-assigned']}>{translate('noLessonsAssigned')}</div>
                                <Divider
                                    className={styles.divider}
                                    orientation="horizontal"
                                    color="rgba(255, 255, 255, 0.2)"
                                />
                                <div className={styles.description}>{translate('noLessonsAssignedDescription')}</div>
                                <Icon name="noAssignedLessons" isPng />
                                <Divider
                                    className={styles['long-divider']}
                                    orientation="horizontal"
                                    color="rgba(255, 255, 255, 0.2)"
                                />
                            </div>
                        ) : (
                            assignedLessons.map((lesson: any) => (
                                <UiLibrary.Grid item key={lesson.uuid}>
                                    <ContentCard
                                        uuid={lesson.uuid}
                                        type={'lesson'}
                                        imageUrl={
                                            lesson.iconUrl ?? `${process.env.PUBLIC_URL}/images/lessonDefaultCard.webp`
                                        }
                                        name={lesson.name}
                                        displayName={lesson.displayName}
                                        description={lesson.description}
                                        timeOrDate={lesson.duration}
                                        cardUrl={lesson.url}
                                        links={getTags(lesson)}
                                        useOverlay={Boolean(lesson.iconUrl)}
                                        status={lesson.isCompleted ? 'completed' : 'notStarted'}
                                        isLeanDisabled={isLeanUser && !lesson.activeOnLean}
                                        earnedPoints={lesson.earnedPoints}
                                        doneRecently={lesson.doneRecently}
                                        score={lesson.doneRecently ? undefined : lesson.potentialPoints}
                                        onContactUsClick={isPLG ? onContactUsClick : undefined}
                                    />
                                </UiLibrary.Grid>
                            ))
                        )}
                    </UiLibrary.Grid>
                </div>
            )}
            <CareerJourneySection learningPaths={learningPaths} skillTrees={skillTrees} />
            <div className={styles.title}>
                <Tabs
                    title={translate('OtherCourses')}
                    currentTabId={tab}
                    onTabClick={(id: number) => {
                        setTab(id);
                    }}
                    tabs={[
                        {
                            id: TabsType.All,
                            title: translate('All'),
                            count: allCount,
                            isNotClickable: allCount === 0
                        },
                        {
                            id: TabsType.InProgress,
                            title: translate('InProgress'),
                            count: inProgressCount,
                            isNotClickable: inProgressCount === 0
                        },
                        {
                            id: TabsType.NotStarted,
                            title: translate('NotStarted'),
                            count: notStartedCount,
                            isNotClickable: notStartedCount === 0
                        },
                        {
                            id: TabsType.Completed,
                            title: translate('Completed'),
                            count: completedCount,
                            isNotClickable: completedCount === 0
                        }
                    ]}
                />
            </div>

            {getCoursesList(getOtherCoursesByTab(tab))}

            {isLeanUser && !isCxUniversity && unavailableCourses.length > 0 && (
                <>
                    <div className={styles.title}>
                        <div className={styles.text}>
                            {translate('AvailableForPremiumUsersOnly')} ({unavailableCourses.length})
                        </div>
                    </div>
                    {getCoursesList(unavailableCourses, true)}
                </>
            )}
        </div>
    );
};

export default Learn;

interface LearnProps {
    isLoading?: boolean;
    primaryCourse: coreService.Course | null;
    assignedLessons: coreService.Lesson[];
    otherCourses: coreService.Course[];
    unavailableCourses: coreService.Course[];
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    setLearningPathCertificateDate: Function;
    learningPaths: LearningPathCard[];
    skillTrees: SkillTreeCard[];
    setVideoTutorialUuid: Function;
    setSkillTreeCertificateDate: Function;
    isLeanUser?: boolean;
    onContactUsClick: Function;
}
